import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Home, Calendar, History, LogOut, CalendarDays, Crown, Sparkles, User } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { cn } from "../lib/utils";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_beginner-gains-app/artifacts/ycyh3qv2_goatgym_logo.png.jpeg";
const APP_VERSION = "v3.1-Premium-Polish";

const MemberLayout = () => {
  const { user, logout, isPremium } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { path: "/hoy", icon: Home, label: "Hoy" },
    { path: "/semana", icon: CalendarDays, label: "Semana" },
    { path: "/programa", icon: Calendar, label: "Programa" },
    { path: "/historial", icon: History, label: "Historial" },
    { path: "/perfil", icon: User, label: "Perfil" },
  ];

  return (
    <div className="min-h-dvh w-full max-w-full bg-[#0B0B0B] flex flex-col">
      {/* Header - with safe area top */}
      <header className="sticky top-0 z-40 w-full bg-[#0B0B0B]/95 backdrop-blur-md border-b border-[#1A1A1A] flex-shrink-0" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="px-3 sm:px-4 h-14 sm:h-16 flex items-center justify-between max-w-full">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <img 
              src={LOGO_URL} 
              alt="GOATGYM" 
              className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0"
            />
            <div className="min-w-0 flex-1">
              <h1 className="font-heading font-black text-base sm:text-lg text-[#D4AF37] leading-tight tracking-tight">
                GOATGYM
              </h1>
              <p className="text-[10px] sm:text-xs text-[#666] truncate max-w-[120px] sm:max-w-none">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            {/* Premium upgrade button - only show for non-premium users */}
            {!isPremium && (
              <button
                onClick={() => navigate("/plan")}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-[#D4AF37] to-amber-500 text-[#0B0B0B] text-xs font-bold rounded-full hover:from-[#E5C54D] hover:to-amber-400 transition-all active:scale-95"
                data-testid="upgrade-premium-btn"
              >
                <Sparkles className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Pasar a Premium</span>
                <span className="sm:hidden">Premium</span>
              </button>
            )}
            {/* Premium badge for premium users */}
            {isPremium && (
              <div className="flex items-center gap-1 px-2 py-1 bg-[#D4AF37]/10 text-[#D4AF37] text-xs font-bold rounded-full border border-[#D4AF37]/30">
                <Crown className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Premium</span>
              </div>
            )}
            <button
              onClick={logout}
              className="p-3 sm:p-3 rounded-xl hover:bg-[#1A1A1A] transition-colors flex-shrink-0 min-w-[44px] min-h-[44px] flex items-center justify-center"
              data-testid="logout-button"
              aria-label="Cerrar sesión"
            >
              <LogOut className="w-5 h-5 text-[#666] hover:text-[#D4AF37]" />
            </button>
          </div>
        </div>
      </header>

      {/* Main content - flexible growth */}
      <main className="px-3 sm:px-4 py-4 sm:py-6 w-full max-w-full flex-1 pb-24">
        <Outlet />
      </main>

      {/* Bottom Navigation - with safe area bottom */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 bg-[#0B0B0B]/95 backdrop-blur-md border-t border-[#1A1A1A]" data-testid="bottom-navigation">
        <div className="h-16 sm:h-20 flex items-center justify-around max-w-lg mx-auto px-2 sm:px-4">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={cn(
                  "flex flex-col items-center justify-center w-16 sm:w-24 h-14 sm:h-16 rounded-xl sm:rounded-2xl transition-all duration-200",
                  isActive
                    ? "bg-[#D4AF37]/10 text-[#D4AF37]"
                    : "text-[#555] hover:text-[#888]"
                )}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <Icon className={cn("w-5 h-5 sm:w-6 sm:h-6 mb-1", isActive && "text-[#D4AF37]")} />
                <span className={cn(
                  "text-[10px] sm:text-xs font-semibold uppercase tracking-wide",
                  isActive && "text-[#D4AF37]"
                )}>
                  {item.label}
                </span>
              </NavLink>
            );
          })}
        </div>
        {/* Safe area spacer for bottom */}
        <div style={{ paddingBottom: 'env(safe-area-inset-bottom)' }} className="bg-[#0B0B0B]" />
      </nav>
    </div>
  );
};

export default MemberLayout;
